'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import 'swagger-ui-react/swagger-ui.css';
import { ThemeToggle } from '@/components/theme-toggle';

const SwaggerUI = dynamic(() => import('swagger-ui-react'), { ssr: false });

// Custom dark theme matching FluxPay brand colors
const customStyles = `
  /* FluxPay obsidian + electric blue/cyan theme */
  .swagger-ui {
    background-color: transparent !important;
    color: hsl(var(--foreground)) !important;
  }

  .swagger-ui .info p,
  .swagger-ui .info li,
  .swagger-ui .info table,
  .swagger-ui .markdown p,
  .swagger-ui .markdown li,
  .swagger-ui .renderedMarkdown p,
  .swagger-ui label,
  .swagger-ui .parameter__type,
  .swagger-ui .response-col_links,
  .swagger-ui .response-col_description {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .wrapper {
    padding: 0 !important;
  }

  /* Info section */
  .swagger-ui .info {
    margin-bottom: 30px !important;
  }

  .swagger-ui .info .title {
    color: hsl(var(--foreground)) !important;
    font-size: 32px !important;
    font-weight: 700 !important;
  }

  .swagger-ui .info .description {
    color: hsl(var(--muted-foreground)) !important;
    font-size: 15px !important;
    line-height: 1.7 !important;
  }

  .swagger-ui .info a {
    color: #06b6d4 !important;
  }

  .swagger-ui .info a:hover {
    color: #67e8f9 !important;
  }

  .swagger-ui .info .base-url {
    color: hsl(var(--muted-foreground)) !important;
  }

  /* Version badge */
  .swagger-ui .info hgroup.main a {
    background: linear-gradient(135deg, #2563eb 0%, #06b6d4 100%) !important;
    padding: 4px 12px !important;
    border-radius: 20px !important;
    color: hsl(var(--primary-foreground)) !important;
    font-size: 12px !important;
  }

  /* Servers dropdown */
  .swagger-ui .scheme-container {
    background: hsl(var(--card) / 0.72) !important;
    border: 1px solid rgba(6, 182, 212, 0.2) !important;
    border-radius: 12px !important;
    padding: 20px !important;
    margin-bottom: 24px !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3) !important;
  }

  .swagger-ui .scheme-container .schemes > label {
    color: hsl(var(--muted-foreground)) !important;
    font-weight: 500 !important;
  }

  .swagger-ui .scheme-container select {
    background: hsl(var(--muted) / 0.86) !important;
    border: 1px solid rgba(6, 182, 212, 0.3) !important;
    border-radius: 8px !important;
    color: hsl(var(--foreground)) !important;
    padding: 8px 12px !important;
  }

  /* Authorize button */
  .swagger-ui .btn.authorize {
    background: linear-gradient(135deg, #2563eb 0%, #06b6d4 100%) !important;
    border: none !important;
    border-radius: 8px !important;
    color: hsl(var(--primary-foreground)) !important;
    font-weight: 600 !important;
    padding: 10px 20px !important;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35) !important;
  }

  .swagger-ui .btn.authorize:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #0891b2 100%) !important;
  }

  .swagger-ui .btn.authorize svg {
    fill: hsl(var(--primary-foreground)) !important;
  }

  /* Operation tags (sections) */
  .swagger-ui .opblock-tag-section {
    margin-bottom: 16px !important;
  }

  .swagger-ui .opblock-tag {
    background: hsl(var(--card) / 0.72) !important;
    color: hsl(var(--foreground)) !important;
    border: 1px solid rgba(6, 182, 212, 0.2) !important;
    border-radius: 12px !important;
    padding: 16px 20px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
  }

  .swagger-ui .opblock-tag:hover {
    background: rgba(6, 182, 212, 0.1) !important;
  }

  .swagger-ui .opblock-tag svg {
    fill: #06b6d4 !important;
  }

  /* Operation blocks */
  .swagger-ui .opblock {
    background: hsl(var(--card) / 0.58) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 12px !important;
    margin-bottom: 12px !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2) !important;
    overflow: hidden !important;
  }

  .swagger-ui .opblock.opblock-post {
    border-left: 4px solid #10b981 !important;
  }

  .swagger-ui .opblock.opblock-post .opblock-summary-method {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%) !important;
  }

  .swagger-ui .opblock.opblock-get {
    border-left: 4px solid #3b82f6 !important;
  }

  .swagger-ui .opblock.opblock-get .opblock-summary-method {
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%) !important;
  }

  .swagger-ui .opblock.opblock-put {
    border-left: 4px solid #f59e0b !important;
  }

  .swagger-ui .opblock.opblock-put .opblock-summary-method {
    background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%) !important;
  }

  .swagger-ui .opblock.opblock-delete {
    border-left: 4px solid #ef4444 !important;
  }

  .swagger-ui .opblock.opblock-delete .opblock-summary-method {
    background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%) !important;
  }

  .swagger-ui .opblock-summary {
    padding: 16px 20px !important;
    background: transparent !important;
  }

  .swagger-ui .opblock-summary-method {
    color: hsl(var(--foreground)) !important;
    font-weight: 700 !important;
    padding: 8px 16px !important;
    border-radius: 8px !important;
    min-width: 80px !important;
    text-align: center !important;
    font-size: 12px !important;
    letter-spacing: 0.5px !important;
  }

  .swagger-ui .opblock-summary-path {
    color: hsl(var(--foreground)) !important;
    font-family: 'Monaco', 'Consolas', monospace !important;
    font-weight: 500 !important;
    font-size: 14px !important;
  }

  .swagger-ui .opblock-summary-path__deprecated {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .opblock-summary-description {
    color: hsl(var(--muted-foreground)) !important;
    font-size: 13px !important;
  }

  /* Expanded operation */
  .swagger-ui .opblock-body {
    background: hsl(var(--muted) / 0.72) !important;
    border-top: 1px solid hsl(var(--muted) / 0.86) !important;
  }

  .swagger-ui .opblock-body pre {
    background: hsl(var(--card)) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 8px !important;
    color: hsl(var(--foreground)) !important;
  }

  .swagger-ui .opblock-description-wrapper p,
  .swagger-ui .opblock-external-docs-wrapper p {
    color: hsl(var(--muted-foreground)) !important;
    line-height: 1.6 !important;
    font-size: 14px !important;
  }

  /* Parameters table */
  .swagger-ui .parameters-col_description {
    color: hsl(var(--muted-foreground)) !important;
    line-height: 1.5 !important;
  }

  .swagger-ui .parameter__name {
    color: #70edca !important;
    font-weight: 600 !important;
  }

  .swagger-ui .parameter__name.required::after {
    color: #f87171 !important;
  }

  .swagger-ui .parameter__type {
    color: #4ade80 !important;
    font-weight: 500 !important;
  }

  .swagger-ui .parameter__in {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui table thead tr th {
    color: hsl(var(--foreground)) !important;
    font-weight: 600 !important;
    background: hsl(var(--card) / 0.72) !important;
    border-bottom: 1px solid hsl(var(--border)) !important;
  }

  .swagger-ui table tbody tr td {
    color: hsl(var(--muted-foreground)) !important;
    vertical-align: top !important;
    padding: 12px 8px !important;
    border-bottom: 1px solid hsl(var(--muted) / 0.86) !important;
  }

  /* Input fields */
  .swagger-ui input[type=text],
  .swagger-ui textarea {
    background: hsl(var(--muted) / 0.86) !important;
    border: 1px solid rgba(6, 182, 212, 0.3) !important;
    border-radius: 8px !important;
    color: hsl(var(--foreground)) !important;
    padding: 10px 14px !important;
  }

  .swagger-ui input[type=text]:focus,
  .swagger-ui textarea:focus {
    border-color: #06b6d4 !important;
    box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.2) !important;
  }

  .swagger-ui select {
    background: hsl(var(--muted) / 0.86) !important;
    border: 1px solid rgba(6, 182, 212, 0.3) !important;
    border-radius: 8px !important;
    color: hsl(var(--foreground)) !important;
  }

  /* Execute button */
  .swagger-ui .btn.execute {
    background: linear-gradient(135deg, #2563eb 0%, #06b6d4 100%) !important;
    color: hsl(var(--primary-foreground)) !important;
    border: none !important;
    border-radius: 8px !important;
    font-weight: 600 !important;
    padding: 10px 24px !important;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35) !important;
  }

  .swagger-ui .btn.execute:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #0891b2 100%) !important;
  }

  .swagger-ui .btn.cancel {
    background: hsl(var(--muted) / 0.86) !important;
    border: 1px solid hsl(var(--input)) !important;
    color: hsl(var(--muted-foreground)) !important;
    border-radius: 8px !important;
  }

  /* Response section */
  .swagger-ui .responses-wrapper {
    background: transparent !important;
  }

  .swagger-ui .response-col_status {
    color: hsl(var(--foreground)) !important;
    font-weight: 600 !important;
  }

  .swagger-ui .response-col_description {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .responses-inner h4,
  .swagger-ui .responses-inner h5 {
    color: hsl(var(--foreground)) !important;
  }

  /* Models/Schemas section */
  .swagger-ui section.models {
    background: hsl(var(--card) / 0.58) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 12px !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  .swagger-ui section.models h4 {
    background: hsl(var(--card) / 0.72) !important;
    color: hsl(var(--foreground)) !important;
    padding: 16px 20px !important;
    margin: 0 !important;
    border-bottom: 1px solid hsl(var(--border)) !important;
  }

  .swagger-ui section.models .model-container {
    background: transparent !important;
    margin: 0 !important;
    padding: 12px 20px !important;
    border-bottom: 1px solid hsl(var(--muted) / 0.86) !important;
  }

  .swagger-ui .model-box {
    background: hsl(var(--card) / 0.58) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 8px !important;
    padding: 12px !important;
  }

  .swagger-ui .model-title {
    color: hsl(var(--foreground)) !important;
    font-weight: 600 !important;
  }

  .swagger-ui .model {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .prop-name {
    color: #70edca !important;
    font-weight: 500 !important;
  }

  .swagger-ui .prop-type {
    color: #4ade80 !important;
  }

  .swagger-ui span.model-toggle::after {
    background: rgba(6, 182, 212, 0.3) !important;
  }

  /* Code blocks */
  .swagger-ui .highlight-code {
    background: hsl(var(--card)) !important;
    border-radius: 8px !important;
  }

  .swagger-ui .highlight-code > .microlight {
    color: hsl(var(--foreground)) !important;
    font-size: 13px !important;
    line-height: 1.6 !important;
  }

  .swagger-ui pre.microlight {
    background: hsl(var(--card)) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 8px !important;
    padding: 16px !important;
  }

  .swagger-ui .copy-to-clipboard {
    background: rgba(6, 182, 212, 0.2) !important;
    border-radius: 6px !important;
  }

  .swagger-ui .copy-to-clipboard:hover {
    background: rgba(6, 182, 212, 0.4) !important;
  }

  /* Markdown content */
  .swagger-ui .renderedMarkdown p {
    color: hsl(var(--muted-foreground)) !important;
    line-height: 1.6 !important;
  }

  .swagger-ui .renderedMarkdown code {
    background: rgba(6, 182, 212, 0.2) !important;
    color: #70edca !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    font-size: 13px !important;
  }

  /* Loading */
  .swagger-ui .loading-container {
    background: transparent !important;
  }

  .swagger-ui .loading-container .loading::before {
    border-color: rgba(6, 182, 212, 0.3) !important;
    border-top-color: #06b6d4 !important;
  }

  /* Arrow icons */
  .swagger-ui svg.arrow {
    fill: hsl(var(--muted-foreground)) !important;
  }

  /* Lock icon */
  .swagger-ui .authorization__btn svg {
    fill: #06b6d4 !important;
  }

  /* Try it out button */
  .swagger-ui .try-out__btn {
    background: transparent !important;
    border: 1px solid rgba(6, 182, 212, 0.5) !important;
    color: #06b6d4 !important;
    border-radius: 6px !important;
  }

  .swagger-ui .try-out__btn:hover {
    background: rgba(6, 182, 212, 0.1) !important;
    border-color: #06b6d4 !important;
  }

  /* Modal dialogs */
  .swagger-ui .dialog-ux .modal-ux {
    background: hsl(var(--card)) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: 16px !important;
  }

  .swagger-ui .dialog-ux .modal-ux-header {
    border-bottom: 1px solid hsl(var(--border)) !important;
  }

  .swagger-ui .dialog-ux .modal-ux-header h3 {
    color: hsl(var(--foreground)) !important;
  }

  .swagger-ui .dialog-ux .modal-ux-content {
    background: hsl(var(--card)) !important;
  }

  .swagger-ui .dialog-ux .modal-ux-content p {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .auth-container {
    background: transparent !important;
  }

  .swagger-ui .auth-container h4 {
    color: hsl(var(--foreground)) !important;
  }

  .swagger-ui .auth-container .wrapper {
    background: transparent !important;
  }

  /* Response examples */
  .swagger-ui .tab li {
    color: hsl(var(--muted-foreground)) !important;
  }

  .swagger-ui .tab li.active {
    color: hsl(var(--foreground)) !important;
  }

  .swagger-ui .tab li button.tablinks {
    color: inherit !important;
    background: transparent !important;
  }

  .swagger-ui .tab li button.tablinks.active {
    background: rgba(6, 182, 212, 0.2) !important;
    border-radius: 6px !important;
  }

  /* Scrollbar */
  .swagger-ui ::-webkit-scrollbar {
    width: 8px !important;
    height: 8px !important;
  }

  .swagger-ui ::-webkit-scrollbar-track {
    background: hsl(var(--muted) / 0.86) !important;
    border-radius: 4px !important;
  }

  .swagger-ui ::-webkit-scrollbar-thumb {
    background: rgba(6, 182, 212, 0.3) !important;
    border-radius: 4px !important;
  }

  .swagger-ui ::-webkit-scrollbar-thumb:hover {
    background: rgba(6, 182, 212, 0.5) !important;
  }

  /* Light-mode safety net. Swagger ships high-specificity dark assumptions in
     several nested selectors, so keep every critical foreground explicit. */
  html:not(.dark) .swagger-ui .info .title,
  html:not(.dark) .swagger-ui .opblock-tag,
  html:not(.dark) .swagger-ui .opblock-summary-path,
  html:not(.dark) .swagger-ui .opblock-summary-path a,
  html:not(.dark) .swagger-ui .model-title,
  html:not(.dark) .swagger-ui .responses-inner h4,
  html:not(.dark) .swagger-ui .responses-inner h5,
  html:not(.dark) .swagger-ui section.models h4,
  html:not(.dark) .swagger-ui table thead tr th {
    color: hsl(var(--foreground)) !important;
  }

  html:not(.dark) .swagger-ui .scheme-container select,
  html:not(.dark) .swagger-ui input,
  html:not(.dark) .swagger-ui textarea {
    color: hsl(var(--foreground)) !important;
    background: hsl(var(--card)) !important;
  }

  html:not(.dark) .swagger-ui .opblock-summary-method {
    color: #ffffff !important;
  }
`;

export default function ApiDocsPage() {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/swagger')
      .then((res) => res.json())
      .then((data) => {
        setSpec(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load API spec:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  if (!spec) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <p className="text-red-400">Failed to load API documentation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Custom CSS for the FluxPay product theme */}
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />

      {/* Header - matching docs page style */}
      <header className="sticky top-0 z-50 bg-background/82 backdrop-blur-2xl border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/favicon.svg" alt="FluxPay" className="w-9 h-9" />
              <div>
                <h1 className="text-lg font-bold"><span className="font-semibold tracking-wide uppercase hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, hsl(var(--foreground)) 25%, #b0d8ff 50%, hsl(var(--foreground)) 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span><span className="font-semibold tracking-wide uppercase inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span> <span className="text-foreground">API</span></h1>
                <p className="text-xs font-medium text-primary">OpenAPI Documentation</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <a
                href="/docs"
                className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-lg font-medium transition"
              >
                Guide
              </a>
              <a
                href="/dashboard"
                className="px-4 py-2 bg-brand-gradient text-primary-foreground rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.8)]"
              >
                Dashboard
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Swagger UI */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SwaggerUI spec={spec} />
      </div>
    </div>
  );
}
